import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Button,
    Box,
    useTheme,
    Typography,
    MenuItem,
    IconButton,
    Tooltip,
    Checkbox,
    InputAdornment,
    Divider,
    FormControl,
    InputLabel,
    Select
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { TableField } from "../types/interfaces";
import { sanitizeText } from "../utils/validators";
import API from "../api/axiosInstance";
import ListEditor from "./ListEditor";

interface FieldOptionsDialogProps {
    open: boolean;
    field: TableField | null;
    onClose: () => void;
    onSave: (updatedField: TableField) => void;
}

const MASKS_BY_TYPE: Record<string, string[]> = {
    Text: ["###-##-####", "(###) ###-####", "#####", "??@??.com", "##/##/####"],
    Number: ["###-###", "$###.##", "00000", "####-####", "###%"],
    Date: ["MM/DD/YYYY", "YYYY-MM-DD", "DD/MM/YYYY"],
    Time: ["HH:mm", "HH:mm:ss"],
    DateTime: ["YYYY-MM-DDTHH:mm:ssZ", "MM/DD/YYYY HH:mm"]
};

const FieldOptionsDialog: React.FC<FieldOptionsDialogProps> = ({ open, field, onClose, onSave }) => {
    const theme = useTheme();
    const [localField, setLocalField] = useState<TableField | null>(null);
    const [testValue, setTestValue] = useState("");
    const [lookupEnabled, setLookupEnabled] = useState(false);
    const [booleanLabels, setBooleanLabels] = useState(["Yes", "No"]);
    const [tables, setTables] = useState<any[]>([]);
    const [availableFields, setAvailableFields] = useState<string[]>([]);
    const [dropdownOptions, setDropdownOptions] = useState<string[]>([]);

    useEffect(() => {
        if (field) {
            const labels = field.dataType === "Boolean" && field.listValues
                ? JSON.parse(field.listValues)
                : ["Yes", "No"];
    
            setBooleanLabels(labels.length === 2 ? labels : ["Yes", "No"]);
    
            if (field.dataType === "Dropdown" && field.listValues) {
                try {
                    const parsed = JSON.parse(field.listValues);
                    setDropdownOptions(Array.isArray(parsed) ? parsed : []);
                } catch {
                    setDropdownOptions([]);
                }
            }
    
            setLocalField({ ...field });
            setLookupEnabled(!!field.lookupTable);
            setTestValue("");
        }
    }, [field]);
    

    useEffect(() => {
        if (lookupEnabled) fetchTables();
    }, [lookupEnabled]);

    useEffect(() => {
        if (lookupEnabled && localField?.lookupTable) {
            fetchFields(localField.lookupTable);
        }
    }, [localField?.lookupTable]);

    const fetchTables = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = {
                headers: {
                    authToken: user.user?.authtoken,
                    sessionId: user.user?.sessionId
                }
            };
            const res = await API.get("/tables", headers);
            setTables(res.data);
        } catch (err) {
            console.error("Failed to fetch tables", err);
        }
    };

    const fetchFields = async (tableId: string) => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = {
                headers: {
                    authToken: user.user?.authtoken,
                    sessionId: user.user?.sessionId
                }
            };
            const res = await API.get(`/tables/${tableId}/fields`, headers);
            setAvailableFields(res.data.map((f: any) => f.fieldName));
        } catch (err) {
            console.error("Failed to fetch fields", err);
        }
    };

    const handleSave = () => {
        if (!localField) return;

        const sanitized: TableField = {
            ...localField,
            fieldName: sanitizeText(localField.fieldName),
            format: sanitizeText(localField.format || ""),
            inputMask: sanitizeText(localField.inputMask || ""),
            defaultValue: sanitizeText(localField.defaultValue || ""),
            displayField: sanitizeText(localField.displayField || "")
        };

        if (!lookupEnabled) {
            sanitized.lookupTable = "";
            sanitized.lookupField = "";
        }

        if (localField.dataType === "Dropdown" && !lookupEnabled) {
            sanitized.listValues = JSON.stringify(dropdownOptions.map(sanitizeText));
        }
        
        if (localField.dataType === "Boolean") {
            sanitized.listValues = JSON.stringify(booleanLabels.map(sanitizeText));
        }
        

        onSave(sanitized);
    };

    if (!localField) return null;

    const relevantMasks = MASKS_BY_TYPE[localField.dataType] || [];
    const applyMaskTest = (value: string): string => {
        if (!localField.inputMask) return value;
        const maskPattern = localField.inputMask.replace(/#/g, "\\d").replace(/\?/g, ".");
        const regex = new RegExp(`^${maskPattern}$`);
        return regex.test(value) ? value : "(Invalid Format)";
    };

    const isDropdown = localField.dataType === "Dropdown";
    const isBoolean = localField.dataType === "Boolean";
    const isFileOrBlob = ["File", "Blob"].includes(localField.dataType);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Field Options: {localField.fieldName || "(New Field)"}</DialogTitle>
            <Typography variant="subtitle2" sx={{ px: 3, mb: 2, color: theme.palette.text.secondary }}>
                Type: {localField.dataType}
            </Typography>
            <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={3}>
                    {!isFileOrBlob && !isBoolean && (
                        <>
                            <Typography variant="subtitle2">Basic Settings</Typography>
                            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                                <TextField
                                    label="Min Value"
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={localField.min ?? ""}
                                    onChange={(e) => setLocalField({ ...localField, min: parseFloat(e.target.value) })}
                                />
                                <TextField
                                    label="Max Value"
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={localField.max ?? ""}
                                    onChange={(e) => setLocalField({ ...localField, max: parseFloat(e.target.value) })}
                                />
                                <TextField
                                    label="Max Length"
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={localField.length || ""}
                                    onChange={(e) => setLocalField({ ...localField, length: parseInt(e.target.value, 10) || 0 })}
                                />
                                <TextField
                                    label="Default Value"
                                    size="small"
                                    fullWidth
                                    value={localField.defaultValue || ""}
                                    onChange={(e) => setLocalField({ ...localField, defaultValue: e.target.value })}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title="Supports functions like Now(), Today(), NextUnique()">
                                                    <IconButton>
                                                        <InfoOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Box>
                        </>
                    )}

                    {!isFileOrBlob && !isBoolean && (
                        <>
                            <Divider />
                            <Typography variant="subtitle2">Masking & Formatting</Typography>
                            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                                <TextField
                                    label="Input Mask"
                                    size="small"
                                    fullWidth
                                    value={localField.inputMask || ""}
                                    onChange={(e) => setLocalField({ ...localField, inputMask: e.target.value })}
                                />
                                <TextField
                                    select
                                    label="Common Mask"
                                    size="small"
                                    fullWidth
                                    value=""
                                    onChange={(e) => setLocalField({ ...localField, inputMask: e.target.value })}
                                >
                                    {relevantMasks.map((mask, i) => (
                                        <MenuItem key={i} value={mask}>{mask}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    label="Test Input"
                                    size="small"
                                    fullWidth
                                    value={testValue}
                                    onChange={(e) => setTestValue(e.target.value)}
                                    helperText={`Result: ${applyMaskTest(testValue)}`}
                                />
                            </Box>
                        </>
                    )}

                    {isDropdown && !lookupEnabled && (
                        <>
                            <Divider />
                            <Typography variant="subtitle2">Dropdown Options</Typography>
                            <ListEditor
                                label="Dropdown Options"
                                items={dropdownOptions}
                                onChange={setDropdownOptions}
                            />
                        </>
                    )}

                    {isBoolean && (
                        <>
                            <Divider />
                            <Typography variant="subtitle2">Boolean Display Options</Typography>
                            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                                <TextField
                                    label="True Value Label"
                                    size="small"
                                    fullWidth
                                    value={booleanLabels[0]}
                                    onChange={(e) => setBooleanLabels([e.target.value, booleanLabels[1]])}
                                />
                                <TextField
                                    label="False Value Label"
                                    size="small"
                                    fullWidth
                                    value={booleanLabels[1]}
                                    onChange={(e) => setBooleanLabels([booleanLabels[0], e.target.value])}
                                />
                            </Box>
                        </>
                    )}

                    {!isFileOrBlob && ["Text", "Number", "Dropdown"].includes(localField.dataType) && (
                        <>
                            <Divider />
                            <Typography variant="subtitle2">Lookup Settings</Typography>
                            <FormControlLabel
                                control={<Checkbox checked={lookupEnabled} onChange={() => setLookupEnabled(!lookupEnabled)} />}
                                label="Lookup From"
                            />
                            {lookupEnabled && (
                                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Select Table</InputLabel>
                                        <Select
                                            value={localField.lookupTable || ""}
                                            onChange={(e) => {
                                                setLocalField({ ...localField, lookupTable: e.target.value, lookupField: "" });
                                            }}
                                        >
                                            {tables.map((table: any) => (
                                                <MenuItem key={table.tableID} value={table.tableID}>
                                                    {table.tableName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Select Field</InputLabel>
                                        <Select
                                            value={localField.lookupField || ""}
                                            onChange={(e) => setLocalField({ ...localField, lookupField: e.target.value })}
                                            disabled={!availableFields.length}
                                        >
                                            {availableFields.map((field, idx) => (
                                                <MenuItem key={idx} value={field}>{field}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}
                        </>
                    )}

                    <Divider />
                    <Typography variant="subtitle2">Field Behavior</Typography>
                    <Box display="flex" gap={4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={localField.required || false}
                                    onChange={() => setLocalField({ ...localField, required: !localField.required })}
                                />
                            }
                            label="Required"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={localField.readOnly || false}
                                    onChange={() => setLocalField({ ...localField, readOnly: !localField.readOnly })}
                                />
                            }
                            label="Read Only"
                        />
                        {!isBoolean && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={localField.isEncrypted || false}
                                        onChange={() => setLocalField({ ...localField, isEncrypted: !localField.isEncrypted })}
                                    />
                                }
                                label="Encrypted"
                            />
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default FieldOptionsDialog;
